import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Track, Playlist } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";
import { BASE_URL_SHARE } from "../../../../constants/api";
import ShareXIcon from "../../../../assets/share-x.svg";
import ShareFBIcon from "../../../../assets/share-fb.svg";
import ShareLinkIcon from "../../../../assets/share-link.svg";
import ShareRedditIcon from "../../../../assets/share-reddit.svg";
import ShareMailIcon from "../../../../assets/share-mail.svg";
import ShareCopyIcon from "../../../../assets/share-copy.svg";

type Props = {
  open: boolean;
  track?: Track | null;
  playlist?: Playlist | null;
  onClose: () => void;
};

export const ShareModal: React.FC<Props> = ({ open, track, playlist, onClose }) => {
  const shareUrl = useMemo(() => {
    if (track) {
      // For tracks: BASE_URL/song/{uuid}
      const uuid =
        (track as any).params?.uuid ??
        (track as any).params?.trackUuid ??
        null;

      if (uuid) {
        return `${BASE_URL_SHARE}/song/${uuid}`;
      }

      // Fallbacks: use numeric/server id or local id if uuid is not available
      const serverId =
        (track as any).params?.sunoTrackId ??
        (track as any).params?.id ??
        (track as any).params?.trackId ??
        track.id;

      console.log('[ShareModal] No uuid for track, falling back to id', {
        trackId: track.id,
        uuid,
        serverId,
        params: (track as any).params,
      });
      return `${BASE_URL_SHARE}/song/${serverId}`;
    }
    if (playlist) {
      // For playlists: BASE_URL/playlist/{uuid}
      if (playlist.uuid) {
        return `${BASE_URL_SHARE}/playlist/${playlist.uuid}`;
      }

      console.log('[ShareModal] No uuid for playlist, falling back to id', {
        playlistId: playlist.id,
        uuid: playlist.uuid,
      });
      return `${BASE_URL_SHARE}/playlist/${playlist.id}`;
    }
    return "";
  }, [track, playlist]);

  const shareTitle = track?.title || playlist?.name || 'Share';

  const handleCopy = () => {
    // Copy the full URL instead of just ID
    navigator.clipboard.writeText(shareUrl).then(() => {
      showToast("Copied to clipboard!", undefined, 'success');
    }).catch(() => {
      showToast("Failed to copy", undefined, 'error');
    });
  };

  const handleShare = (platform: string) => {
    const url = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(shareTitle);
    
    let platformUrl = '';
    
    switch (platform) {
      case 'x':
        platformUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'facebook':
        platformUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        platformUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'reddit':
        platformUrl = `https://reddit.com/submit?url=${url}&title=${title}`;
        break;
      case 'mail':
        platformUrl = `mailto:?subject=${title}&body=${url}`;
        break;
    }
    
    if (platformUrl) {
      window.open(platformUrl, '_blank', 'width=600,height=400');
    }
  };

  if (!open || (!track && !playlist)) return null;

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>Share to...</Title>
        
        <IconsRow>
          <IconButton onClick={() => handleShare('x')}>
            <IconWrapper>
              <ShareXIcon />
            </IconWrapper>
          </IconButton>
          <IconButton onClick={() => handleShare('facebook')}>
            <IconWrapper>
              <ShareFBIcon />
            </IconWrapper>
          </IconButton>
          <IconButton onClick={() => handleShare('linkedin')}>
            <IconWrapper>
              <ShareLinkIcon />
            </IconWrapper>
          </IconButton>
          <IconButton2 onClick={() => handleShare('reddit')}>
            <IconWrapper2>
              <ShareRedditIcon />
            </IconWrapper2>
          </IconButton2>
          <IconButton2 onClick={() => handleShare('mail')}>
            <IconWrapper2>
              <ShareMailIcon />
            </IconWrapper2>
          </IconButton2>
        </IconsRow>

        <LinkSection>
          <LinkInput
            type="text"
            value={shareUrl}
            readOnly
          />
          <CopyButton onClick={handleCopy}>
            <CopyIconWrapper>
              <ShareCopyIcon />
            </CopyIconWrapper>
            <CopyText>Copy</CopyText>
          </CopyButton>
        </LinkSection>
        
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 500px;
  max-width: calc(100% - 40px);
  background: #fff;
  color: #000;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
    padding: 24px 20px;
  }
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  text-align: center;
`;

const IconsRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;

const IconButton = styled.button`
  width: 37px;
  height: 37px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
  }
`;
const IconButton2 = styled.button`
  width: 31px;
  height: 31px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
  }
`;
    const IconWrapper = styled.div`
    width: 37px;
    height: 37px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 37px;
        height: 37px;
    }
    `;
    const IconWrapper2 = styled.div`
    width: 31px;
    height: 31px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
      width: 31px;
      height: 31px;
    }
  `;
const LinkSection = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: #7987FF;
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f4f4f4;
  }
`;

const CopyIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CopyText = styled.span`
  font-size: 14px;
  color: #333;
`;

const ShareButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
    border-color: #7987FF;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ShareButtonText = styled.span`
  font-size: 14px;
  color: #333;
`;

export default ShareModal;

